import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// Removed Modal import since we're using a custom fullscreen layout
import CryptoJS from 'crypto-js';

// Components
import DigitCodeInput from '../common/DigitCodeInput';
import PrimaryButton from '../common/Button/PrimaryButton';
import TitleHeader from '../common/TitleHeader';

// Services and utilities
import { OtpService, StorageKeys } from '../../services/otp.service';
import { authService } from '../../services/auth.service';
import { showAlert } from '../../utils/toast';
import { getStableHash } from '../../utils/utils';
import { LocalKeys } from '../../hooks/useLocalData';
import { fetchApi } from '../../utils/api';
import { AppRouteEnum } from '../../routes/routeEnums';

/**
 * Type definition for verification steps
 */
type VerifyStep = 'pin' | 'otp';

/**
 * Props for the SmartOTP Modal component
 */
interface SmartOTPModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onError?: () => void;
  from?: string;
}

const getLocalStorageProfile = () => {
  const profileData = localStorage.getItem(LocalKeys.PROFILE_DATA);
  return profileData ? JSON.parse(profileData) : null;
};

const resolveUsername = (): string => {
  try {
    const profileData = getLocalStorageProfile();
    const usernameFromProfile = (profileData?.Username || '').trim();
    if (usernameFromProfile) return usernameFromProfile;
  } catch {
    // ignore
  }
  return (authService.getUsername() || '').trim();
};

/**
 * SmartOTPModal Component
 * A modal version of the Smart OTP verification flow
 */
const SmartOTPModal: React.FC<SmartOTPModalProps> = ({ 
  visible, 
  onClose,
  onSuccess,
  onError,
  from
}) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<VerifyStep>('pin');
  
  // Form state
  const [pinCode, setPinCode] = useState<string[]>(Array(6).fill(''));
  const [otpCode, setOtpCode] = useState<string[]>(Array(6).fill(''));
  
  // UI state
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number>(60);
  const [isTimerExpired, setIsTimerExpired] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [shouldRender, setShouldRender] = useState<boolean>(false);
  
  // API state
  const [sharedToken, setSharedToken] = useState<string>('');

  // Reset component state when modal opens
  useEffect(() => {
    if (visible) {
      // Reset to initial step
      setShouldRender(true);
      setCurrentStep('pin');
      setPinCode(Array(6).fill(''));
      setOtpCode(Array(6).fill(''));
      setIsLoading(false);
      setCountdown(60);
      setIsTimerExpired(false);
      setSharedToken('');
      
      // Delay showing content for animation
      setTimeout(() => setIsVisible(true), 10);
      
      // Check if OTP is set up
      checkOtpSetup();
    } else {
      // Handle closing animation
      setIsVisible(false);
      // Wait for exit animation to complete before unmounting
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [visible]);

  // Check if Smart OTP is set up
  const checkOtpSetup = async () => {
    const profileData = getLocalStorageProfile();
    if (!profileData) {
      console.error('Profile data is not available');
      showAlert('error', 'Không thể xác định thông tin người dùng, vui lòng đăng nhập lại');
      if (onError) {
        onError();
      }
      onClose();
      return;
    }

    if (profileData.Username) {
      try {
        // Check if user has OTP set up directly with service
        const isSetup = await OtpService.isOtpSetup(profileData.Username);
        
        if (!isSetup) {
          showAlert(
            'error', 
            'Vui lòng thiết lập Smart OTP trước khi sử dụng tính năng này',
            {
              text: 'Cài đặt SmartOTP',
              onClick: () => navigate(AppRouteEnum.SMART_OTP_SETUP)
            }
          );
          if (onError) {
            onError();
          }
          onClose();
        }
      } catch (error) {
        console.error('Error checking OTP setup:', error);
        showAlert('error', 'Không thể kiểm tra trạng thái Smart OTP');
        if (onError) {
          onError();
        }
        onClose();
      }
    } else {
      console.error('Username is not available in profile data');
      showAlert('error', 'Không thể xác định tên đăng nhập, vui lòng đăng nhập lại');
      if (onError) {
        onError();
      }
      onClose();
    }
  };
  
  // Countdown timer for OTP
  useEffect(() => {
    let timerInterval: NodeJS.Timeout | null = null;
    
    // Only run countdown when on OTP step
    if (currentStep === 'otp' && countdown > 0) {
      setIsTimerExpired(false);
      
      timerInterval = setInterval(() => {
        setCountdown(prevCountdown => {
          if (prevCountdown <= 1) {
            clearInterval(timerInterval!);
            setIsTimerExpired(true);
            return 0;
          }
          return prevCountdown - 1;
        });
      }, 1000);
    }
    
    // Cleanup timer when component unmounts or step changes
    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [currentStep, countdown]);

  // Generate OTP based on PIN and sharedToken
  const generateOtp = async (pin: string, tokenValue: string) => {
    try {
      // Decrypt the shared token using PIN
      const token = getStableHash(CryptoJS.TripleDES.decrypt(tokenValue, pin).toString(CryptoJS.enc.Utf8));
      const time = await OtpService.getOtpServerTime();

      // Generate the OTP using token and Vietnam time (UTC+7), matching server DateTime.Now
      const newOTP = getStableHash(`${token}.${OtpService.formatOtpTimestamp(time)}`);
      const newOtpArray = newOTP.split('');
      setOtpCode(newOtpArray);

      try {
        const logData = {
          newOTP,
          time: time.toISOString(),
          serverToken: CryptoJS.TripleDES.decrypt(tokenValue, pin).toString(CryptoJS.enc.Utf8),
          hashTime: `${token}.${OtpService.formatOtpTimestamp(time)}`
        }
        const resp = await fetchApi(
          'post',
          `api/Account/Log?message=${encodeURIComponent(JSON.stringify(logData))}`,
          {},
          'json',
          { skipCache: true }
        )
        console.log(logData)
        console.log(`Resp Log: ${resp}`)
      } catch (e) {
        console.log(e)
      }
      
      // Reset countdown
      setCountdown(60);
      setIsTimerExpired(false);
      
      return true;
    } catch (error: any) {
      console.error('Error generating OTP:', error);
      showAlert('error', error.message || 'Không thể tạo mã OTP mới');
      return false;
    }
  };

  // Function to regenerate OTP - identical to VerifySmartOTP
  const regenerateOtp = async () => {
    // We need the PIN to decrypt the shared token
    const pin = pinCode.join('');
    if (!pin || pin.length !== 6) {
      // Go back to PIN step if PIN is not available
      setCurrentStep('pin');
      showAlert('warning', 'Vui lòng nhập lại mã PIN để tạo mã OTP mới');
      return;
    }
    
    // Use the stored shared token to regenerate OTP
    if (sharedToken) {
      await generateOtp(pin, sharedToken);
    } else {
      // If for some reason we don't have the shared token, go back to PIN step
      setCurrentStep('pin');
      showAlert('warning', 'Vui lòng nhập lại mã PIN để tạo mã OTP mới');
    }
  };

  // Verify PIN
  const verifyPin = async () => {
    // Check if PIN is valid
    const pin = pinCode.join('');
    
    if (pin.length !== 6 || !/^\d+$/.test(pin)) {
      showAlert('error', 'Vui lòng nhập đủ 6 chữ số cho mã PIN');
      return;
    }
    
    const username = resolveUsername();
    if (!username) {
      showAlert('error', 'Không thể xác định tên đăng nhập, vui lòng thử lại');
      if (onError) {
        onError();
      }
      onClose();
      return;
    }
    
    // Verify PIN against stored encrypted key
    const isPinValid = OtpService.verifyPin(username, pin);
    
    if (isPinValid) {
      try {
        // Get sharedToken from OTP_SECRETS
        const secretsJson = localStorage.getItem(StorageKeys.OTP_SECRETS);
        if (!secretsJson) {
          throw new Error('Không tìm thấy thông tin Smart OTP, vui lòng thiết lập lại Smart OTP');
        }
        
        // Parse the JSON and get the user's secrets
        const secrets = JSON.parse(secretsJson);
        const candidateUsernames = Array.from(
          new Set([username, username.toLowerCase(), username.toUpperCase()].map(v => (v || '').trim()).filter(Boolean))
        );
        const userSecrets = candidateUsernames.map(u => secrets[u]).find(Boolean);
        
        if (!userSecrets || !userSecrets.SHARED_TOKEN) {
          throw new Error('Không tìm thấy token chia sẻ, vui lòng thiết lập lại Smart OTP');
        }
        
        // Store shared token for later regeneration
        const tokenValue = userSecrets.SHARED_TOKEN;
        setSharedToken(tokenValue);
        
        // Generate OTP and move to OTP step
        const success = await generateOtp(pin, tokenValue);
        if (success) {
          setCurrentStep('otp');
        }
      } catch (error: any) {
        console.error('Error generating OTP:', error);
        showAlert('error', error.message || 'Không thể tạo mã OTP mới');
      }
    } else {
      showAlert('error', 'Vui lòng kiểm tra lại mã PIN của bạn');
    }
  };

  // Verify OTP
  const verifyOtp = async () => {
    // Check if OTP is valid
    const otp = otpCode.join('');
    
    if (otp.length !== 6 || !/^\d+$/.test(otp)) {
      showAlert('error', 'Vui lòng nhập đủ 6 chữ số cho mã OTP');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Verify OTP with API
      const response = await OtpService.verifySmartOTP(otp);
      
      // Handle success
      if (response?.Status === 'Success') {
        const username = resolveUsername();
        if (username) {
          OtpService.saveLastSmartOtpCode(otp, username);
        } else {
          // still save globally for the submit step to pick up
          OtpService.saveLastSmartOtpCode(otp);
        }

        // Match VerifySmartOTP: no success alert to avoid double messages
        console.log('OTP verification successful');
        
        // Close modal and notify parent of success
        onSuccess();
        onClose();
      } else {
        throw new Error(response?.Message || 'Xác thực OTP thất bại');
      }
    } catch (error: any) {
      console.error('OTP verification failed:', error);
      showAlert('error', error.message || 'Không thể xác thực mã OTP');
    } finally {
      setIsLoading(false);
    }
  };

  // We'll use same layout as VerifySmartOTP component

  // Render PIN verification step
  const renderPinStep = () => (
    <>
      <div className="px-4 pt-4 flex-1 overflow-y-auto">
        <div className="mb-8">
          <h3 className="text-lg font-bold mb-4">Nhập mã PIN Smart OTP</h3>
          <DigitCodeInput 
            length={6} 
            value={pinCode} 
            onChange={setPinCode} 
            prefix="pin" 
          />
        </div>
      </div>
    </>
  );

  // Render OTP verification step
  const renderOtpStep = () => (
    <>
      <div className="px-4 pt-4 flex-1 overflow-y-auto">
        <div className="mb-8">
          <h3 className="text-lg font-bold mb-4">Nhập mã Smart OTP</h3>
          <DigitCodeInput
            length={6}
            value={otpCode}
            onChange={setOtpCode}
            prefix="otp"
          />
          
          {/* Countdown timer */}
          <div className="mt-4 text-center">
            {isTimerExpired ? (
              <button 
                onClick={regenerateOtp}
                className="text-blue-600 font-medium hover:underline"
              >
                Gửi lại mã
              </button>
            ) : (
              <p className="text-gray-600">
                Mã OTP còn hiệu lực trong <span className="font-bold">{countdown}</span> giây
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );

  /**
   * Handle back button click for multi-step navigation
   * @returns boolean - true if navigation was handled internally, false to use default navigation
   */
  const handleBackClick = (): boolean => {
    // If we're on the OTP step, go back to the PIN step
    if (currentStep === 'otp') {
      setCurrentStep('pin');
      // Reset OTP inputs
      setOtpCode(Array(6).fill(''));
      return true;
    }
    
    // If we're on the PIN step, close the modal
    onClose();
    return true;
  };

  // Render nothing if shouldn't render
  if (!shouldRender) return null;
  
  // Early validation of profile data before rendering the modal
  // This prevents rendering the modal UI if profileData is not available
  if (!getLocalStorageProfile()) {
    console.error('SmartOTPModal: Profile data is missing');
    // We still call the effects by rendering an empty div
    // This ensures checkOtpSetup will run and properly handle the error
    return <div className="hidden"></div>;
  }
  
  return (
    <div 
      className={`fixed inset-0 bg-white z-[20000] flex flex-col h-full w-full transition-all duration-300 ease-in-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
      style={{ height: '100%', minHeight: '-webkit-fill-available' }}
    >
      {/* Title Header */}
      <TitleHeader 
        title="Xác thực Smart OTP" 
        onBackClick={handleBackClick}
        showPrevIcon={true}
      />
      
      {/* Content - Fixed height without scrolling */}
      <div className="flex flex-col flex-1 pt-[70px] pb-[80px] justify-center overflow-hidden">
        {currentStep === 'pin' ? renderPinStep() : renderOtpStep()}
      </div>
      
      {/* Button Container - Enhanced visibility for all screen sizes */}
      <div className="fixed left-0 right-0 bottom-0 p-4 bg-white z-[20001] border-t border-gray-200 shadow-lg">
        <div className="max-w-md mx-auto w-full pb-4">
          {currentStep === 'pin' ? (
            <PrimaryButton
              onClick={verifyPin} 
              disabled={pinCode.some(d => d === '')}
              isDefaultSubmit={true}
            >
              Tiếp tục
            </PrimaryButton>
          ) : (
            <PrimaryButton 
              onClick={verifyOtp} 
              disabled={isLoading || otpCode.some(d => d === '')}
              isDefaultSubmit={true}
            >
              {isLoading ? 'Đang xử lý...' : 'Xác thực'}
            </PrimaryButton>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmartOTPModal;
